import { createContext, useContext, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { listOrganisations } from '@/data/queries'
import type { Org } from '@/data/queries'
import { useAsync } from '@/data/useAsync'
import { Failed, Loading } from '@/components/bits'

interface OrgValue {
  orgs: Org[]
  current: Org
  select: (id: string) => void
}

const OrgContext = createContext<OrgValue | null>(null)

const STORAGE_KEY = 'tfh-hr.org'

export function OrgProvider({ children }: { children: ReactNode }) {
  const result = useAsync(() => listOrganisations(), [])
  const [selected, setSelected] = useState<string | null>(() => {
    try {
      return window.localStorage.getItem(STORAGE_KEY)
    } catch {
      return null
    }
  })

  const value = useMemo<OrgValue | null>(() => {
    if (result.state !== 'ready' || result.data.length === 0) return null
    const orgs = result.data
    const current = orgs.find((o) => o.id === selected) ?? orgs[0]!
    return {
      orgs,
      current,
      select: (id) => {
        setSelected(id)
        try {
          window.localStorage.setItem(STORAGE_KEY, id)
        } catch {
          // A remembered choice is a convenience, not a requirement.
        }
      },
    }
  }, [result.state, result.state === 'ready' ? result.data : null, selected])

  if (result.state === 'loading') return <Loading what="your organisations" />
  if (result.state === 'error') return <Failed message={result.message} onRetry={result.reload} />
  if (value === null) {
    return (
      <Failed message="You are signed in, but no organisation is visible to your account. Someone with HR access needs to add a role assignment for you." />
    )
  }

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>
}

export function useOrg(): OrgValue {
  const value = useContext(OrgContext)
  if (value === null) throw new Error('useOrg must be used inside OrgProvider')
  return value
}

export function orgName(org: Org): string {
  return org.tradingName ?? org.legalName
}
